import React, { Fragment } from 'react';

import { memoize } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  SBL_5198_CUB_BULK_ENTRY,
  SHOW_OFFLINE_FEATURES,
  SHOW_QUICK_ENTRY,
} from '@config';
import { programSel } from '@context/duck/selectorsTyped';
import { AddIndividualDropdown } from '@modules/advancement/common/components/AddIndividualDropdown';
import { filterSel } from '@modules/advancement/packRoster/duck/selectorsTyped';
import { PackRosterFilters } from '@modules/advancement/packRoster/types';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { isInactiveUnitSel } from '@modules/unit/duck/selectorsTyped';
import { SyncPendingButton } from '@offline';
import { ConditionalRender, FilterButton } from '@shared/components';
import { Program } from '@shared/constants';
import { hasNewPermissionSel } from '@shared/duck/selectors';
import { newPermissions } from '@shared/newPermissions';

import { QuickEntryDropdown, RunReportDropdown } from '../../../../common';
import { AddSubUnitButton, GroupSubUnitButton } from '../../../../subUnits';
import PackRosterFilterCard from '../PackRosterFilterCard';
import styles from './PackRosterTable.less';
import RecordProgressDropdown from './RecordProgressDropdown';

interface PackRosterListTopHeaderProps {
  selectedItems: number[];
  selectedRowKeys: number[];
  isOffline: boolean;
}
const getFilterName = memoize((filter: PackRosterFilters): string => {
  const allChecked = Object.values(filter).every(f => !!f);
  return allChecked ? 'showAllRoster' : 'showFiltered';
});

const PackRosterTableHeader = ({
  selectedItems,
  selectedRowKeys,
  isOffline,
}: PackRosterListTopHeaderProps) => {
  const selectedLength = selectedRowKeys.length;
  const unitProgram = useSelector(programSel);
  const isInactiveUnit = useSelector(isInactiveUnitSel);
  const filter = useSelector(filterSel);
  const canAddSubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_UNIT),
  );
  const canAddDenChief = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.ADD_DEN_CHIEF),
  );
  const filterName = getFilterName(filter);
  return (
    <React.Fragment>
      <ConditionalRender value={!isInactiveUnit}>
        {unitProgram === Program.CUB_SCOUT &&
          canAddDenChief &&
          featureFlags.getFlag('SBL_4688_ADD_INDIVIDUAL') && (
            <AddIndividualDropdown disabled={isOffline} />
          )}
        {canAddSubUnits && (
          <AddSubUnitButton size="small" color="white" ghost noBorder />
        )}
        {(!!SHOW_QUICK_ENTRY || SBL_5198_CUB_BULK_ENTRY) && (
          <QuickEntryDropdown disabled={isOffline} />
        )}
        <RunReportDropdown disabled={isOffline} showAllUsers={false} />
        <RecordProgressDropdown
          disabled={
            selectedLength === 0 || (!SHOW_OFFLINE_FEATURES && isOffline)
          }
          selectedItems={selectedItems}
          recordProgressEnabled
        />
        <GroupSubUnitButton />
        {!!SHOW_OFFLINE_FEATURES && (
          <SyncPendingButton className={styles.syncPending} />
        )}
      </ConditionalRender>
      <PackRosterFilterCard>
        <FilterButton className="" rightPadding={false}>
          <Fragment>
            <span className={styles.filterShow}>
              <FormattedMessage id="advancement.PackRoster.show" />:
            </span>
            <FormattedMessage id={`advancement.PackRoster.${filterName}`} />
          </Fragment>
        </FilterButton>
      </PackRosterFilterCard>
    </React.Fragment>
  );
};

export default PackRosterTableHeader;
